<template>
	<VInput
		ref="company-short-name-input"
		v-model="company.additionalAttributes.nameShort.value"
		label="Сокращенное наименование"
		layout="vertical"
		required
		:disabled="readOnly"
	/>

	<VSelect
		ref="company-tax-system-input"
		v-model="company.taxSystem"
		label="Режим налогообложения"
		layout="vertical"
		required
		:options="taxSystemList"
		:clearable="false"
		:disabled="readOnly"
	>
		<template #helper-text>
			<template v-if="isESHN">
				Ставку НДС по этому типу Вы сможете указать для каждого артикула из Вашего
				ассортимента при заполнении нового Предложения
			</template>
		</template>
	</VSelect>

	<VCheckbox
		v-model="company.isVatPayer"
		:disabled="!isESHN && !isSTS || readOnly"
	>
		Плательщик НДС
	</VCheckbox>

	<VSelect
		v-if="isSTS && company.isVatPayer"
		v-model="company.vatRate"
		label="Ставка НДС"
		layout="vertical"
		required
		:options="CompaniesService.stsVatRates"
		:disabled="readOnly"
	/>

	<VInput
		ref="company-inn-input"
		v-model="company.additionalAttributes.inn.value"
		label="ИНН"
		layout="vertical"
		required
		:precision="0"
		:validate-method="RuUtils.validateINN"
		:disabled="readOnly"
	/>

	<VInputMask
		v-if="!isIpInn"
		ref="company-kpp-input"
		label="КПП"
		layout="vertical"
		required
		raw
		:tokens="{
        '#': { pattern: /\d/ },
        N: { pattern: /[0-9A-Z]/ },
      }"
		mask="####-NN-###"
		:value="company.additionalAttributes.kpp.value"
		@input="onKppInput"
		:disabled="readOnly"
	/>

	<VInput
		v-if="!isIpInn"
		ref="company-ogrn-input"
		v-model="company.additionalAttributes.ogrn.value"
		label="ОГРН"
		layout="vertical"
		required
		:validate-method="RuUtils.validateOGRN"
		:disabled="readOnly"
	/>

	<VInput
		v-if="isIpInn"
		ref="company-ogrnip-input"
		v-model="company.additionalAttributes.ogrnip.value"
		label="ОГРНИП"
		layout="vertical"
		required
		:validate-method="RuUtils.validateOGRNIP"
		:disabled="readOnly"
	/>

	<VCheckbox
		v-model="company.isUsingEdi"
		:disabled="readOnly"
	>
		Работаю с электронным документооборотом (ЭДО)

		<template #helper-text>
			<template v-if="company.isUsingEdi">
				Подтверждаю, что работаю с ЭДО по обмену юридически значимыми документами и имею
				цифровую подпись
			</template>
			<template v-else>
				Отметьте если компания работает с электронным документооборотом юридически значимых
				документов, подписываемых ЭЦП
			</template>
		</template>
	</VCheckbox>

  <EdiOperatorConnectionSelect
    v-if="company.isUsingEdi"
    v-model="company.ediConnections"
    :edi-operators="ediOperatorsList"
    :disabled="readOnly"
  />

	<VTextarea
		ref="company-address-de-facto-input"
		v-model="company.additionalAttributes.addressDeFacto.value"
		label="Фактический адрес"
		layout="vertical"
		required
		@input="onAddressDeFactoInput"
		:disabled="readOnly"
	/>

	<VCheckbox
		v-model="isAddressDeFactoNotTheSame.addressDeJure"
		@input="onSameAddressInput($event, 'addressDeJure')"
		:disabled="readOnly"
	>
		Юридический адрес отличается от фактического
	</VCheckbox>

	<VTextarea
		v-if="isAddressDeFactoNotTheSame.addressDeJure"
		ref="company-address-de-jure-input"
		v-model="company.additionalAttributes.addressDeJure.value"
		layout="vertical"
		:label="company.additionalAttributes.addressDeJure.name"
		required
		:disabled="readOnly"
	/>

	<VCheckbox
		v-model="isAddressDeFactoNotTheSame.addressPostal"
		@input="onSameAddressInput($event, 'addressPostal')"
		:disabled="readOnly"
	>
		Почтовый адрес отличается от фактического
	</VCheckbox>

	<VTextarea
		v-if="isAddressDeFactoNotTheSame.addressPostal"
		ref="company-address-postal-input"
		v-model="company.additionalAttributes.addressPostal.value"
		required
		:label="company.additionalAttributes.addressPostal.name"
		layout="vertical"
		:disabled="readOnly"
	/>
</template>

<script>

import Company        	from '@/domains/Companies/Company.ts';
import CompaniesService from '@/domains/Companies/CompaniesService.ts';

import RuUtils        	from '@/core/utils/regional/RuUtils'
import EdiOperatorsService from "@/domains/EdiOperators/EdiOperatorsService.js";
import EdiOperatorConnectionSelect
  from "@/domains/EdiOperators/components/EdiOperatorConnectionSelect.vue";

export default {
	name: 'RUAdditionalAttributes',
  components: {EdiOperatorConnectionSelect},

	props: {
		company: {
			type:     Company,
			required: true
		},
		readOnly: {
			type: Boolean,
			default: false,
		}
	},

	data() {
		const isAddressDeFactoNotTheSame = {
			addressDeJure:
				this.company.additionalAttributes.addressDeFacto.value !== this.company.additionalAttributes.addressDeJure.value,
			addressPostal:
				this.company.additionalAttributes.addressDeFacto.value !== this.company.additionalAttributes.addressPostal.value
		};

		return {
			CompaniesService,
			isAddressDeFactoNotTheSame,
			RuUtils,
		};
	},

	computed: {
		isESHN() {
			return this.company?.taxSystem?.name === 'ЕСХН';
		},
		/** STS - Simplified taxation system (Упрощенная система налогообложения) */
		isSTS() {
			return this.company?.taxSystem?.name === 'УСН';
		},
		isIpInn() {
			if (this.company.country.code !== 'RU') return null;

			return (
				typeof this.company.additionalAttributes.inn.value === 'string'
				&& this.company.additionalAttributes.inn.value.length === 12
			);
		},
		taxSystemList() {
			return CompaniesService.companyTaxSystems;
		},
		ediOperatorsList() {
			return EdiOperatorsService.ediOperators.toArray();
		},
	},
	methods: {
		onKppInput(value) {
			this.company.additionalAttributes.kpp.value = value.raw;
		},

		onAddressDeFactoInput(value) {
			/** Если адрес де факто равен адресу де юро */
			if (!this.isAddressDeFactoNotTheSame.addressDeJure) {
				this.company.additionalAttributes.addressDeJure.value = value;
			}
			/** Если адрес де факто равен почтовому */
			if (!this.isAddressDeFactoNotTheSame.addressPostal) {
				this.company.additionalAttributes.addressPostal.value = value;
			}

		},
		onSameAddressInput(value, propName) {
			if (value === false) {
				this.company.additionalAttributes[propName].value = this.company.additionalAttributes.addressDeFacto.value;
			}
		},
	}
};
</script>
